/**
 * Les lectures du tableau de bord général, et elles seules.
 *
 * Aucune n'écrit. Chacune est indépendante : l'écran les lance ensemble et
 * affiche chaque bloc dès qu'il arrive, ou dit qu'il est illisible — une vue
 * en panne ne vide pas tout le tableau.
 *
 * Les comptes passent par `count: 'exact', head: true` : le nombre, sans
 * rapatrier les lignes, et sans le plafond de mille lignes de PostgREST qui
 * ferait passer « 1000 » pour une mesure (voir `countPendingPayments`).
 */

import { db } from '../payments/db';
import { messageErreur } from '../payments/erreur';
import { sansJpa, stagesDesJpa } from '../jpa/stages.supabase';
import type { Demandes, DernierImport, SessionAVenir } from './general';

export { countPendingPayments as compterLesMouvementsATraiter } from '../payments/actions.supabase';

function echec(e: unknown, defaut: string): never {
  throw new Error(messageErreur(e, defaut));
}

/**
 * Les sessions qui ne sont pas terminées, actives et non archivées.
 * `current_registrations` est tenu par la base ; vérifié le 27/09/2026 sur
 * les 69 sessions à venir : il concorde partout avec le comptage des
 * inscriptions non annulées. Les journées pédagogiques n'y sont pas : ce bloc
 * dit le remplissage des stages, et leurs 9999 places « sans limite » le
 * faussaient (30 587 places restantes le 24/10/2026).
 */
export async function lireLesSessionsAVenir(aujourdhui: string): Promise<{ sessions: SessionAVenir[]; tronque: boolean }> {
  const jpa = await stagesDesJpa();
  const { data, error, count } = await sansJpa(db
    .from('sessions')
    .select('id, start_date, end_date, capacity, current_registrations, stage:stages(title), center:centers(name)', { count: 'exact' })
    .gte('end_date', aujourdhui)
    .eq('active', true)
    .eq('archived', false)
    .order('start_date', { ascending: true })
    .order('id', { ascending: true })
    .limit(1000), jpa);
  if (error) echec(error, 'Sessions illisibles');
  const lignes = (data ?? []) as Record<string, unknown>[];
  const nom = (o: unknown, k: string) =>
    o && typeof o === 'object' && typeof (o as Record<string, unknown>)[k] === 'string'
      ? ((o as Record<string, unknown>)[k] as string)
      : null;
  return {
    sessions: lignes.map((r) => ({
      id: r.id as string,
      debut: r.start_date as string,
      fin: r.end_date as string,
      stage: nom(r.stage, 'title'),
      centre: nom(r.center, 'name'),
      capacite: Number(r.capacity ?? 0),
      inscrits: Number(r.current_registrations ?? 0),
    })),
    tronque: (count ?? 0) > lignes.length,
  };
}

async function compter(requete: PromiseLike<{ count: number | null; error: unknown }>, defaut: string): Promise<number> {
  const { count, error } = await requete;
  if (error) echec(error, defaut);
  return count ?? 0;
}

export async function lireLesDemandes(aujourdhui: string): Promise<Demandes> {
  const [annulations, plusAncienne, inclusions, inclusionsAVenir, attente] = await Promise.all([
    compter(
      db.from('cancellation_requests').select('id', { count: 'exact', head: true }).eq('status', 'pending'),
      'Demandes d’annulation illisibles',
    ),
    db.from('cancellation_requests').select('request_date').eq('status', 'pending')
      .order('request_date', { ascending: true }).limit(1),
    compter(
      db.from('inclusion_requests').select('id', { count: 'exact', head: true }).eq('status', 'pending'),
      'Demandes d’inclusion illisibles',
    ),
    compter(
      db.from('inclusion_requests').select('id, sessions!inner(end_date)', { count: 'exact', head: true })
        .eq('status', 'pending').gte('sessions.end_date', aujourdhui),
      'Demandes d’inclusion illisibles',
    ),
    compter(
      db.from('waiting_list').select('id, sessions!inner(end_date)', { count: 'exact', head: true })
        .in('status', ['waiting', 'invited']).gte('sessions.end_date', aujourdhui),
      'Liste d’attente illisible',
    ),
  ]);
  if (plusAncienne.error) echec(plusAncienne.error, 'Demandes d’annulation illisibles');
  const premiere = ((plusAncienne.data ?? []) as { request_date: string | null }[])[0];
  return {
    annulationsEnAttente: annulations,
    annulationPlusAncienne: premiere?.request_date ?? null,
    inclusionsEnAttente: inclusions,
    inclusionsSurSessionsAVenir: inclusionsAVenir,
    listeAttenteSurSessionsAVenir: attente,
  };
}

/** `null` : aucun import n'a jamais eu lieu. */
export async function lireLeDernierImport(): Promise<DernierImport | null> {
  const { data, error } = await db
    .from('payment_imports')
    .select('created_at, statement_date, period_to')
    .order('created_at', { ascending: false })
    .limit(1);
  if (error) echec(error, 'Imports bancaires illisibles');
  const r = ((data ?? []) as Record<string, unknown>[])[0];
  if (!r) return null;
  return {
    creeLe: r.created_at as string,
    dateReleve: (r.statement_date as string | null) ?? null,
    periodeFin: (r.period_to as string | null) ?? null,
  };
}

export interface PaiementRecu {
  id: string;
  dateValeur: string | null;
  montantCents: number;
  source: string;
  contrepartie: string | null;
}

export async function lireLesDerniersPaiements(nombre = 5): Promise<PaiementRecu[]> {
  const { data, error } = await db
    .from('payments')
    .select('id, value_date, amount_cents, source, counterparty_name')
    .gt('amount_cents', 0)
    .order('value_date', { ascending: false, nullsFirst: false })
    .order('created_at', { ascending: false })
    .limit(nombre);
  if (error) echec(error, 'Paiements illisibles');
  return ((data ?? []) as Record<string, unknown>[]).map((r) => ({
    id: r.id as string,
    dateValeur: (r.value_date as string | null) ?? null,
    montantCents: Number(r.amount_cents ?? 0),
    source: String(r.source ?? ''),
    contrepartie: (r.counterparty_name as string | null) ?? null,
  }));
}
